import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useIdParam } from "../../useIdParam"
import { getGroupLogo } from "../../../library/utils/getGroupLogo"
import { call } from "../../../library/networking/API"
import bgDefault from "../../../assets/img/bg-default.png"
import React from "react"
import { useTry } from "../../useTry"
import { GROUPS } from "../../../library/store/actions/types/groupsTypes"

export const useGroup = (
  {
    id,
    participants_limit = 5,
    connections_limit = 5,
    products_limit = 5,
  } = {},
  options = {}
) => {
  const idParam = useIdParam("groupId")

  const __id = id || idParam || null

  const queryKey = [GROUPS.GET, { id: __id }]

  const { data: group, ...query } = useQuery({
    queryKey,
    queryFn: async (ctx) => {
      const res = await call(ctx.queryKey[0], {
        id: __id,
        participants_limit,
        connections_limit,
        products_limit,
      })

      return res.data
    },
    enabled: options.enabled || !!__id,
    refetchOnWindowFocus: false,
    staleTime: 60000,
    ...options,
  })

  return {
    group,
    groupHeader: group?.header || bgDefault,
    groupLogo: getGroupLogo(group),
    isEducational: [4, 5, 7].includes(group?.type),
    isGovEducational: [4, 5].includes(group?.type),
    isDistrict: group?.type === 5,
    isSchool: group?.type === 4,
    isOneTeacherSchool: group?.type === 7,
    queryKey,
    ...query,
  }
}

export const useGroupProductAttachments = (options = {}) => {
  const groupId = useIdParam("groupId")

  const query = useQuery({
    queryKey: [GROUPS.LIBRARY_LIST, { group_id: groupId }],
    queryFn: async (ctx) => {
      const data = await call(ctx.queryKey[0], {
        group_id: groupId,
        offset: 0,
        limit: 5,
      })

      return data?.products
    },
    refetchOnWindowFocus: false,
    ...options,
  })
  return { ...query }
}

export const useGroupMutations = ({ queryKey }) => {
  const queryClient = useQueryClient()
  const safeCall = useTry()

  const updateGroup = useMutation({
    mutationFn: async ({
      id,
      name,
      description,
      website,
      country,
      city,
      state,
      address,
      zip_code,
      phone,
      start_date,
      end_date,
      full_image,
      full_header,
      closed,
      attachments,
      links,
      district_id,
    }) => {
      const group = queryClient.getQueryData(queryKey)

      const __attachments = attachments || [
        ...group.videos.map((f) => f.id),
        ...group.blobs.map((f) => f.id),
        ...group.images.map((f) => f.id),
      ]
      await safeCall(async () => {
        await call(GROUPS.UPDATE, {
          id,
          name,
          description,
          website,
          country,
          city,
          state,
          address,
          zip_code,
          phone,
          start_date,
          end_date,
          full_image,
          full_header,
          closed,
          attachments: __attachments,
          links,
          district_id,
        })
      })
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
  })

  const deleteGroup = useMutation({
    mutationFn: async (id) => {
      await safeCall(async () => {
        await call(GROUPS.DELETE, { id })
      })
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
  })

  return {
    updateGroup,
    deleteGroup,
  }
}

export const useGroupRelations = ({ queryKey, isMutatingInfinite = true }) => {
  const queryClient = useQueryClient()
  const safeCall = useTry()

  const [mutatingIds, setMutatingIds] = React.useState([])

  const joinGroup = useMutation({
    mutationFn: async (id) => {
      await safeCall(async () => await call(GROUPS.JOIN, { id }))
    },
    onSuccess: async (data, user_id, ctx) => {
      if (!isMutatingInfinite) {
        return queryClient.invalidateQueries({ queryKey })
      }
    },
  })

  const leaveGroup = useMutation({
    mutationFn: async (id) => {
      await safeCall(async () => await call(GROUPS.LEAVE, { id }))
    },
    onSuccess: async (data, user_id, ctx) => {
      if (!isMutatingInfinite) {
        return queryClient.invalidateQueries({ queryKey })
      }
    },
  })

  const cancelRequest = useMutation({
    mutationFn: async (group_id) => {
      await safeCall(
        async () =>
          await call(GROUPS.PARTICIPANTS.REQUESTS.CANCEL, {
            group_id,
          })
      )
    },
    onSuccess: async (data, user_id, ctx) => {
      if (!isMutatingInfinite) {
        return queryClient.invalidateQueries({ queryKey })
      }
    },
  })

  return {
    mutatingIds,
    joinGroup,
    leaveGroup,
    requests: {
      cancel: cancelRequest,
    },
  }
}
